const MS_PER_DAY = 86400000;

export type PayrollScheduleBiweekly = { type: 'biweekly'; anchorPayDate: string };
export type PayrollScheduleDates = { type: 'dates'; payDates: string[] };
export type PayrollSchedule = PayrollScheduleBiweekly | PayrollScheduleDates;

export type LeaveBucketKey = 'sick' | 'vacation' | 'paidTimeOff' | 'unpaidTimeOff';

export const LEAVE_BUCKET_KEYS: LeaveBucketKey[] = ['sick', 'vacation', 'paidTimeOff', 'unpaidTimeOff'];

export function utcMidnightMs(ymd: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) throw new Error(`Invalid yyyy-MM-dd: ${ymd}`);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function calendarDaysBetweenYmd(a: string, b: string): number {
  return Math.round((utcMidnightMs(b) - utcMidnightMs(a)) / MS_PER_DAY);
}

/** Calendar date in a specific IANA zone (for scheduler parity with Cloud Function). */
export function ymdInTimeZone(d: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const y = parts.find(p => p.type === 'year')?.value;
  const mo = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;
  if (!y || !mo || !day) throw new Error('Intl date parts missing');
  return `${y}-${mo}-${day}`;
}

export function isPayDateForSchedule(schedule: PayrollSchedule | null | undefined, ymd: string): boolean {
  if (!schedule) return false;
  if (schedule.type === 'dates') {
    const dates = [...(schedule.payDates || [])].sort();
    return dates.includes(ymd);
  }
  if (schedule.type === 'biweekly') {
    const anchor = schedule.anchorPayDate?.trim();
    if (!anchor) return false;
    try {
      const n = calendarDaysBetweenYmd(anchor, ymd);
      return n % 14 === 0;
    } catch {
      return false;
    }
  }
  return false;
}

/** Every calendar day in [start, end] (inclusive) that is a pay date under the schedule. */
export function listPayDatesInRange(
  schedule: PayrollSchedule | null | undefined,
  start: Date,
  end: Date,
): string[] {
  if (!schedule) return [];
  const out: string[] = [];
  const s0 = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e0 = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  for (let t = s0.getTime(); t <= e0.getTime(); t += MS_PER_DAY) {
    const d = new Date(t);
    const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (isPayDateForSchedule(schedule, ymd)) out.push(ymd);
  }
  return out;
}

/** Next pay dates after `fromYmd` (exclusive), for UI preview; capped. */
export function nextPayDatesAfter(
  schedule: PayrollSchedule | null | undefined,
  fromYmd: string,
  maxCount: number,
): string[] {
  if (!schedule || maxCount <= 0) return [];
  const out: string[] = [];
  let cursor = utcMidnightMs(fromYmd);
  const limitMs = cursor + MS_PER_DAY * 400;
  while (out.length < maxCount && cursor < limitMs) {
    cursor += MS_PER_DAY;
    const d = new Date(cursor);
    const ymd = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    if (isPayDateForSchedule(schedule, ymd)) out.push(ymd);
  }
  return out;
}

/**
 * Average paydays per calendar month for scaling loaded monthly payroll into
 * a per-paycheck amount. Biweekly ≈ 26/year → treat as 2/month so a 3-payday
 * month charges 1.5× the average monthly payroll (full check each payday).
 */
export function typicalPaydaysPerMonth(schedule: PayrollSchedule): number {
  if (schedule.type === 'biweekly') return 2;
  return 2;
}

/** Full paycheck amount from an average-month loaded payroll total. */
export function payrollAmountPerPayday(
  monthlyPayroll: number,
  schedule: PayrollSchedule,
): number {
  const typical = typicalPaydaysPerMonth(schedule);
  if (typical <= 0) return Math.max(0, Math.round(monthlyPayroll));
  return Math.max(0, Math.round(monthlyPayroll / typical));
}

/** Scale average-month payroll up/down by how many paydays fall in the month. */
export function scaleMonthlyPayrollForPaydays(
  monthlyPayroll: number,
  paydayCount: number,
  schedule: PayrollSchedule,
): number {
  const perPayday = payrollAmountPerPayday(monthlyPayroll, schedule);
  return Math.max(0, perPayday * Math.max(0, paydayCount));
}

export function parsePayrollSchedule(raw: unknown): PayrollSchedule | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const typeRaw = typeof o.type === 'string' ? o.type.trim().toLowerCase() : '';
  if (typeRaw === 'biweekly') {
    const anchor = typeof o.anchorPayDate === 'string' ? o.anchorPayDate.trim() : '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(anchor)) return null;
    return { type: 'biweekly', anchorPayDate: anchor };
  }
  if (typeRaw === 'dates') {
    const payDates = Array.isArray(o.payDates)
      ? o.payDates.filter((x): x is string => typeof x === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(x))
      : [];
    if (payDates.length === 0) return null;
    return { type: 'dates', payDates: Array.from(new Set(payDates)).sort() };
  }
  return null;
}

export function parseDefaultAccrualPerPayRun(raw: unknown): Partial<Record<LeaveBucketKey, number>> | null {
  if (!raw || typeof raw !== 'object') return null;
  const keys = LEAVE_BUCKET_KEYS;
  const out: Partial<Record<LeaveBucketKey, number>> = {};
  const o = raw as Record<string, unknown>;
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
  }
  return Object.keys(out).length ? out : null;
}

export function emptyLeaveBuckets(): Record<
  LeaveBucketKey,
  { available: number; accrualPerPayRun: number }
> {
  return {
    sick: { available: 0, accrualPerPayRun: 0 },
    vacation: { available: 0, accrualPerPayRun: 0 },
    paidTimeOff: { available: 0, accrualPerPayRun: 0 },
    unpaidTimeOff: { available: 0, accrualPerPayRun: 0 },
  };
}
